'use client';

/**
 * Feature Flags 功能开关状态管理
 * 全局管理功能开关，控制用户可见功能
 */
import { create } from 'zustand';
import { API_BASE_URL } from '@/lib/api/client';
import { authFetch } from '@/lib/auth/session';
import { useAuthStore } from '@/stores/authStore';
import { registerAuthScopedCacheReset } from '@/lib/auth-scoped-state-reset';

export type FeatureFlagKey =
  // Global
  | 'sidebar_admin' | 'social_publishing' | 'director_board' | 'visual_editor_prompt_templates' | 'auth_wechat_qr_login' | 'chat_reasoning_trace_v1'
  // Payment
  | 'payment_topup' | 'payment_subscription'
  // Toolbar groups
  | 'toolbar_video' | 'toolbar_image' | 'toolbar_transform' | 'toolbar_color' | 'toolbar_edit'
  // Video capabilities
  | 'toolbar_lip_sync' | 'toolbar_image_to_video' | 'toolbar_text_to_video'
  | 'toolbar_video_extend' | 'toolbar_multi_image_to_video'
  // Image capabilities
  | 'toolbar_face_swap' | 'toolbar_skin_enhance'
  | 'toolbar_relight'
  // Transform capabilities
  | 'toolbar_angle' | 'toolbar_image_to_3d'
  // Color capabilities
  | 'toolbar_color_transfer' | 'toolbar_palette_gen' | 'toolbar_local_recolor';

interface FeatureFlagsStore {
  flags: Record<string, boolean>;
  loading: boolean;
  initialized: boolean;
  /** 初始化（只请求一次） */
  initFlags: () => Promise<void>;
  /** 强制刷新 */
  refetchFlags: () => Promise<void>;
  clearFlags: () => void;
}

// A response from a previous account or an older refresh cannot restore stale flags.
let flagsRequestRevision = 0;

/** 在组件中读取单个开关（reactive selector） */
export function useFeatureFlag(key: FeatureFlagKey): boolean {
  return useFeatureFlagsStore((s) => (
    key === 'payment_topup' || key === 'payment_subscription' ? true : s.flags[key] ?? false
  ));
}

/** 普通用户可见性门禁：Admin 始终放行，开关只控制普通用户入口。 */
export function useUserVisibleFeatureFlag(key: FeatureFlagKey): boolean {
  const enabled = useFeatureFlag(key);
  const isAdmin = useAuthStore((s) => s.user?.isAdmin ?? false);
  return isAdmin || enabled;
}

// 页面回到前台时自动刷新功能开关，覆盖后台开关变更后的长驻页面场景
if (typeof window !== 'undefined') {
  let lastRefetchAt = 0;
  const THROTTLE_MS = 5_000;

  const handleVisibilityOrFocus = () => {
    if (document.visibilityState === 'hidden') return;

    const now = Date.now();
    if (now - lastRefetchAt < THROTTLE_MS) return;

    lastRefetchAt = now;
    const { initialized } = useFeatureFlagsStore.getState();
    if (initialized) {
      useFeatureFlagsStore.getState().refetchFlags();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityOrFocus);
  window.addEventListener('focus', handleVisibilityOrFocus);
}

export const useFeatureFlagsStore = create<FeatureFlagsStore>((set, get) => ({
  flags: {},
  loading: false,
  initialized: false,

  initFlags: async () => {
    if (get().initialized) return;
    set({ loading: true, initialized: true });
    try {
      await get().refetchFlags();
    } finally {
      set({ loading: false });
    }
  },

  refetchFlags: async () => {
    const revision = ++flagsRequestRevision;
    const userId = useAuthStore.getState().user?.id;
    try {
      const res = await authFetch(`${API_BASE_URL}/users/me/feature-flags`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data: Record<string, boolean> = await res.json();
      if (revision !== flagsRequestRevision || userId !== useAuthStore.getState().user?.id) return;
      set({ flags: data });
    } catch {
      // 静默失败 — 功能开关查询不应阻断主流程
    }
  },

  clearFlags: () => {
    flagsRequestRevision += 1;
    set({ flags: {}, initialized: false, loading: false });
  },
}));

registerAuthScopedCacheReset('feature-flags', () => useFeatureFlagsStore.getState().clearFlags());
